import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  Divider,
  FlexDirection,
  FontFamily,
  Join,
  Space,
} from '@gousto-internal/citrus-react'

import type { TableFields } from './types'

export type TableProps = TableFields

function getCellFlexBasis(columnCount: number): string {
  return `${100 / Math.max(columnCount, 1)}%`
}

export function Table({ heading, columns, rows }: TableProps) {
  if (rows.length === 0) return null

  const columnCount = columns.length > 0 ? columns.length : rows[0]?.cells.length ?? 1

  return (
    <>
      {heading && (
        <Box paddingBottom={4}>
          <Body2 fontFamily={FontFamily.Bold} size={4}>
            {heading}
          </Body2>
        </Box>
      )}

      <Box
        display={[Display.None, Display.Block]}
        style={{ borderRadius: '8px' }}
        boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
        bg={Color.White}
        width="100%"
      >
        {columns.length > 0 && (
          <Box bg={Color.ColdGrey_600} display={Display.Flex} paddingH={4} paddingV={3}>
            {columns.map((column, index) => (
              <Box key={index} flexBasis={getCellFlexBasis(columnCount)}>
                <Body2 size={2} fontFamily={FontFamily.Bold} color={Color.White}>
                  {column.header}
                </Body2>
              </Box>
            ))}
          </Box>
        )}

        <Join with={<Divider />}>
          {rows.map((row) => (
            <Box key={row.row_id} display={Display.Flex} paddingH={4} paddingV={3}>
              {row.cells.slice(0, columnCount).map((cell, index) => (
                <Box
                  key={index}
                  flexBasis={getCellFlexBasis(columnCount)}
                  paddingRight={index < columnCount - 1 ? 3 : 0}
                >
                  {cell.is_bold ? (
                    <Body2 size={2} fontFamily={FontFamily.Bold}>
                      {cell.value}
                    </Body2>
                  ) : (
                    <Body2 size={2}>{cell.value}</Body2>
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Join>
      </Box>

      <Box display={[Display.Block, Display.None]}>
        <Join with={<Space size={4} />}>
          {rows.map((row) => (
            <Box
              key={row.row_id}
              style={{ borderRadius: '8px' }}
              boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
              bg={Color.White}
            >
              {row.cells[0] && (
                <Box
                  bg={Color.ColdGrey_600}
                  style={{ borderRadius: '8px 8px 0 0' }}
                  paddingH={4}
                  paddingV={3}
                >
                  <Body2 size={4} fontFamily={FontFamily.Bold} color={Color.White}>
                    {row.cells[0].value}
                  </Body2>
                </Box>
              )}

              <Box paddingH={4} paddingV={3}>
                {row.cells.slice(1, columnCount).map((cell, index) => {
                  const columnIndex = index + 1
                  const columnLabel = columns[columnIndex]?.header ?? ''

                  return (
                    <React.Fragment key={index}>
                      {index > 0 && <Divider />}
                      <Box
                        paddingH={2}
                        paddingV={2}
                        display={Display.Flex}
                        flexDirection={FlexDirection.Row}
                      >
                        {columnLabel && (
                          <>
                            <Box flexBasis="30%">
                              <Body2 fontFamily={FontFamily.Bold}>{columnLabel}</Body2>
                            </Box>
                            <Box flexBasis="70%">
                              <Body2 size={2}>{cell.value}</Body2>
                            </Box>
                          </>
                        )}
                        {!columnLabel && (
                          <Box flexBasis="100%">
                            {cell.is_bold ? (
                              <Body2 size={2} fontFamily={FontFamily.Bold}>
                                {cell.value}
                              </Body2>
                            ) : (
                              <Body2 size={2}>{cell.value}</Body2>
                            )}
                          </Box>
                        )}
                      </Box>
                    </React.Fragment>
                  )
                })}
              </Box>
            </Box>
          ))}
        </Join>
      </Box>
    </>
  )
}
